// agreementsRepo.js — data layer for the Agreements document library (Phase 1).
//
// Backs the Member/LC read view and the GC "Manage Agreements" tool. The base
// agreement list still comes from the code constant (agreementData.AGREEMENTS) so
// the page can never render empty; this layer adds the Firestore/Storage lifecycle
// on top: versioned document uploads, effective dates, archive, forward, and audit.
//
// Custodian model (enforced server-side in firestore.rules / storage.rules):
//   • an agreement record is owned by ONE custodianCommittee
//   • only that committee's general_chair (or admin) may create/version/archive it
//   • nothing hard-deletes — retiring is status:'archived'
// This module assumes those rules are deployed; it does not re-implement security.
//
// Storage layout: agreements/{committee}/{agreementKey}/v{version}/{fileName}

import { db, storage } from '../firebase';
import {
  doc, getDoc, getDocs, collection, serverTimestamp, writeBatch, query, where,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { versionsWithRatification } from './ratification';   // WO-001 S10 (section 249)
import { looksDerived, DERIVED_UPLOAD_REFUSED, attestationFields } from './agreementCustody';   // Record 589 (audit): ruling 211   // Record 599: the attestation
import { LC_ROSTER } from './lcRoster';
import { DEFAULT_CRAFT } from './craftTerms';   // ruling 312 phase 1 (c)

// A stable Firestore doc id for a code-defined agreement (numeric id -> 'a2').
export const keyForCodeAgreement = (id) => 'a' + String(id);

// ---------------------------------------------------------------------------
// AUDIT — attributed, immutable. Matches the audit_logs rule (exact key set,
// server timestamp, action in the allow-list). Non-fatal on failure.
// ---------------------------------------------------------------------------
// G3.4 (2026-09-15, task list section 251; audit trail 3.6 findings 2.2). This used to be a separate,
// best-effort setDoc AFTER the mutation - "non-fatal on failure", a console.warn nobody reads - with a
// row id built from performance.now(), which is milliseconds since PAGE LOAD and collides across tabs
// and users; the colliding write was an update, denied, and lost silently. Now: the audit row is a
// document with a Firestore-generated id (no collision), and it is written IN THE SAME BATCH as the
// mutation it records (auditedWrite below), so a mutation whose audit row is refused is itself
// refused - the trail is mandatory, not best-effort (ruling 79's principle, the same one route (a)
// applied to claims). The failure is loud: the caller's await throws and the screen shows it.
export function auditRow(user, action, targetId, note) {
  if (!user || !user.uid) throw new Error('an agreement act is audited under a signed-in account, and none is signed in');
  return {
    ref: doc(collection(db, 'audit_logs')),
    data: { actorId: user.uid, at: serverTimestamp(), action, targetType: 'agreement', targetId: String(targetId || ''), note: String(note || '') },
  };
}
// One batch: the mutation(s) the caller adds, plus the audit row. Commits together or not at all.
export async function auditedWrite(user, action, targetId, note, addMutations) {
  const b = writeBatch(db);
  addMutations(b);
  const a = auditRow(user, action, targetId, note);
  b.set(a.ref, a.data);
  await b.commit();
  return a.ref.id;
}
// Kept as a name for anything that still calls it; it is now loud and atomic on its own.
export async function logAudit(user, action, targetId, note) {
  return auditedWrite(user, action, targetId, note, () => {});
}

// ---------------------------------------------------------------------------
// READ
// ---------------------------------------------------------------------------

// RULING 192 PART 1 (2026-09-19). Whose agreement records a reader may list: the administrator all of them;
// anyone else only their own committee's; an account with no committee none. Pure; tested in
// scripts\\test-agreements-scope.mjs. The rule refuses an unfiltered listing, so the query must be shaped.
export function agreementRecordsScope(user) {
  if (!user) return { mode: 'none' };
  if (user.isAdmin || user.role === 'admin') return { mode: 'all' };
  const c = typeof user.committee === 'string' ? user.committee.trim() : '';
  return c ? { mode: 'committee', committee: c } : { mode: 'none' };
}

// Load the agreement lifecycle records this reader may see, keyed by doc id.
// RULING 263 (Bob, 2026-09-30): "Full access to the NCC agreements should be available to everyone to view in the same
// manner as the individual agreements." The national agreements (National Carriers' Conference Committee rounds -
// Section 6 settlements) belong to no one committee; they are held under the custodian NATIONAL, read by every
// signed-in account, and written only by the administrator (firestore.rules / storage.rules, AGR-2 unchanged for
// committee agreements).
export const NATIONAL = 'NATIONAL';
export async function loadNationalAgreements() {
  const out = [];
  try {
    const snap = await getDocs(query(collection(db, 'agreements'), where('custodianCommittee', '==', NATIONAL)));
    snap.forEach(d => out.push({ key: d.id, ...d.data() }));
  } catch (e) {
    console.warn('[agreementsRepo] loadNationalAgreements failed:', e.message);
  }
  return out.filter(r => r.status !== 'archived').sort((a, b) => String(a.effectiveDate || '').localeCompare(String(b.effectiveDate || '')));
}

export async function loadAgreementRecords(user) {
  const out = {};
  const scope = agreementRecordsScope(user);
  if (scope.mode === 'none') return out;
  try {
    const snap = await getDocs(scope.mode === 'all'
      ? collection(db, 'agreements')
      : query(collection(db, 'agreements'), where('custodianCommittee', '==', scope.committee)));
    snap.forEach(d => { out[d.id] = { key: d.id, ...d.data() }; });
  } catch (e) {
    console.warn('[agreementsRepo] loadAgreementRecords failed:', e.message);
  }
  return out;
}

// The current (most recent, non-archived) version entry of a record.
export function currentVersion(record) {
  if (!record || !Array.isArray(record.versions) || record.versions.length === 0) return null;
  const v = record.versions.find(x => x.version === record.currentVersion);
  return v || record.versions[record.versions.length - 1];
}

// Which version governs an event on `incidentDate` (ISO 'YYYY-MM-DD').
// The version with the latest effectiveDate that is <= the incident date.
// Prior versions stay authoritative for earlier events; the newest takes over
// automatically on its effective date — no timer, resolution is by date.
export function versionEffectiveOn(record, incidentDate) {
  if (!record || !Array.isArray(record.versions)) return null;
  const when = incidentDate ? String(incidentDate).slice(0, 10) : '9999-12-31';
  const eligible = record.versions
    .filter(v => (v.effectiveDate || '0000-00-00') <= when)
    .sort((a, b) => (a.effectiveDate || '').localeCompare(b.effectiveDate || ''));
  return eligible.length ? eligible[eligible.length - 1] : null;
}

// A time-limited download URL for a version's file.
export async function fileUrl(version) {
  if (!version || !version.filePath) return null;
  try {
    return await getDownloadURL(ref(storage, version.filePath));
  } catch (e) {
    console.warn('[agreementsRepo] fileUrl failed:', e.message);
    return null;
  }
}

// ---------------------------------------------------------------------------
// SCOPING — "My Agreements". Which agreements a person may see, by role.
//   admin        → everything
//   general_chair→ their committee's agreements
//   local_chair  → agreements whose railroad is in their locals' railroads
//   member       → agreements matching the railroad(s) on their profile
// Agreements are governing contracts (rules allow any authed read); this is
// relevance scoping for the UI, not a security boundary.
// ---------------------------------------------------------------------------
export function railroadsForLocalChair(user) {
  const scopes = (user && Array.isArray(user.scopes)) ? user.scopes : [];
  const set = new Set();
  scopes.forEach(sk => {
    LC_ROSTER.filter(r => r.scopeKey === sk).forEach(r => set.add(r.railroadKey));
  });
  return Array.from(set);
}

export function scopeAgreements(agreements, user, memberRailroads) {
  if (!user) return [];
  if (user.isAdmin || user.role === 'admin') return agreements;
  if (user.role === 'general_chair') {
    const cmte = user.committee;
    return cmte ? agreements.filter(a => a.committee === cmte) : agreements;
  }
  if (user.role === 'local_chair') {
    const rr = railroadsForLocalChair(user);
    return rr.length ? agreements.filter(a => rr.includes(a.railroadKey)) : agreements;
  }
  // member (or tester): match profile railroad(s); fall back to all if unknown
  const rr = Array.isArray(memberRailroads) ? memberRailroads.filter(Boolean) : [];
  if (!rr.length) return agreements;
  return agreements.filter(a => rr.includes(a.railroadKey));
}

// Is this user the custodian who may manage this agreement?
export function canManage(user, agreement) {
  if (!user) return false;
  if (user.isAdmin || user.role === 'admin') return true;
  return user.role === 'general_chair' && !!user.committee
      && user.committee === (agreement.custodianCommittee || agreement.committee);
}

// ---------------------------------------------------------------------------
// WRITE — create / new version / archive. All go through the custodian rules.
// ---------------------------------------------------------------------------

// Upload a file as the next version of an agreement, creating the record if it
// doesn't exist yet. Prior versions are kept and marked archived (history is
// never destroyed). Returns the updated record shape (best-effort).
export async function uploadVersion(user, base, file, effectiveDate, attested) {
  if (!user || !file) throw new Error('missing user or file');
  // Record 589 (audit), ruling 211: a machine-written restatement is never an agreement. Refused BEFORE
  // anything is read, stored or written - no Storage object, no record, no version, no audit row.
  if (looksDerived(file.name)) throw new Error(DERIVED_UPLOAD_REFUSED);
  // Record 599 (Bob, 2026-10-07), ruling 211: the uploader's attestation, ticked on the screen. Refused, like the
  // derived name, before anything is read, stored or written.
  const att = attestationFields(user, attested);
  if (!att.ok) throw new Error(att.because);
  const committee = base.custodianCommittee || base.committee;
  if (!committee) throw new Error('agreement has no committee');
  const key = base.key || keyForCodeAgreement(base.id);
  const recRef = doc(db, 'agreements', key);
  const snap = await getDoc(recRef);
  const exists = snap.exists();
  const prev = exists ? snap.data() : null;
  const nextVersion = exists ? (Number(prev.currentVersion) || 0) + 1 : 1;

  const filePath = 'agreements/' + committee + '/' + key + '/v' + nextVersion + '/' + file.name;
  await uploadBytes(ref(storage, filePath), file);

  const entry = {
    version: nextVersion,
    fileName: file.name,
    filePath,
    effectiveDate: effectiveDate || '',
    uploadedBy: user.uid,
    uploadedByName: user.name || user.email || 'GC',
    uploadedAt: new Date().toISOString(),
    archived: false,
    ...att.fields,   // Record 599: attestedBy, attestedByName, attestationText on the version itself
  };
  // Record 599: the attestation with SERVER time. A server timestamp cannot sit inside the versions array, so the
  // full record (with attestedAt) is kept per version under attestations.v<N>; the rules require it there.
  const attestation = { ...att.fields, attestedAt: serverTimestamp() };

  if (!exists) {
    await auditedWrite(user, 'agreement_add', key, 'v1 ' + file.name, b => b.set(recRef, {
      title: base.title || '',
      committee,
      custodianCommittee: committee,
      railroadKey: base.railroadKey || '',
      carrier: base.carrier || base.railroadKey || '',
      craft: base.craft || DEFAULT_CRAFT,
      source: base.source || 'code',
      status: 'active',
      currentVersion: nextVersion,
      effectiveDate: effectiveDate || '',
      versions: [entry],
      attestations: { ['v' + nextVersion]: attestation },   // Record 599
      createdBy: user.uid,
      createdByName: user.name || user.email || 'GC',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }));
  } else {
    const versions = (Array.isArray(prev.versions) ? prev.versions : [])
      .map(v => ({ ...v, archived: true }))
      .concat(entry);
    await auditedWrite(user, 'agreement_version', key, 'v' + nextVersion + ' ' + file.name, b => b.update(recRef, {
      versions,
      currentVersion: nextVersion,
      ['attestations.v' + nextVersion]: attestation,   // Record 599: adds this version's; earlier ones untouched
      effectiveDate: effectiveDate || prev.effectiveDate || '',
      status: 'active',
      updatedAt: serverTimestamp(),
    }));
  }
  return key;
}

// Create a brand-new agreement (not in the code constant) with its first version.
export async function createAgreement(user, meta, file, effectiveDate, attested) {
  if (!user) throw new Error('no user');
  if (file && looksDerived(file.name)) throw new Error(DERIVED_UPLOAD_REFUSED);   // Record 589 (audit): ruling 211, before a key is minted
  const att = attestationFields(user, attested);   // Record 599: the attestation, before a key is minted
  if (!att.ok) throw new Error(att.because);
  const committee = meta.committee || (user.committee) || '';
  if (!committee) throw new Error('committee required');
  const key = 'custom-' + committee + '-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);   // G3.4: a clock and entropy, not performance.now() (per-page-load, collides)
  return uploadVersion(user, {
    key,
    title: meta.title,
    committee,
    custodianCommittee: committee,
    railroadKey: meta.railroadKey || '',
    carrier: meta.carrier || meta.railroadKey || '',
    craft: meta.craft || DEFAULT_CRAFT,
    source: 'custom',
  }, file, effectiveDate, attested);
}

// Archive (soft-retire) an agreement. Nothing is deleted.
export async function archiveAgreement(user, key) {
  const recRef = doc(db, 'agreements', key);
  await auditedWrite(user, 'agreement_archive', key, 'archived', b => b.update(recRef, {
    status: 'archived',
    archivedBy: user.uid,
    archivedByName: user.name || user.email || '',
    archivedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }));
}

// Restore an archived agreement to active.
export async function restoreAgreement(user, key) {
  const recRef = doc(db, 'agreements', key);
  await auditedWrite(user, 'agreement_restore', key, 'restored', b => b.update(recRef, {   // G3.4: its own word, not archive-with-a-note
    status: 'active',
    archivedBy: null,
    archivedAt: null,
    updatedAt: serverTimestamp(),
  }));
}

// WO-001 S10 (2026-09-14, task list section 249): the ratification record on one version. The
// decision is src\data\ratification.js's (planRatification / versionsWithRatification); this writes the
// versions array back under the agreement's own update rule (custodian GC or admin) and logs it.
export async function recordRatification(user, record, versionNo, ratificationRecord) {
  if (!user || !record || !record.key) throw new Error('missing user or agreement record');
  const v = versionsWithRatification(record.versions, versionNo, ratificationRecord);
  if (!v.ok) throw new Error(v.errors.join(' '));
  await auditedWrite(user, 'agreement_ratification', record.key, 'v' + versionNo + ' ' + (ratificationRecord.kind === 'none' ? 'no ratification required: ' + ratificationRecord.reason : ratificationRecord.result + ' ' + ratificationRecord.polledOn + ' (' + ratificationRecord.votesFor + '-' + ratificationRecord.votesAgainst + ')'),
    b => b.update(doc(db, 'agreements', record.key), { versions: v.versions, updatedAt: serverTimestamp() }));
}
